import type { BlotterCashEntry } from '@/store/blotter/types/BlotterCashEntry.ts';
import { cashRepriceQuote } from '@/store/blotter/workflows/cashRepriceQuote/action';
import { initiateCheckCashByTradeCapture } from '@/store/cash/api/index.ts';
import { effectOfAddTileActionOnESP } from '@/store/cash/listeners/esp-listener.ts';
import type { FxCashForward, FxCashNdf, FxCashSpot } from '@/store/cash/state.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { swapApi } from '@/store/swap/api/index.ts';
import type { FxSwap } from '@/store/swap/state.ts';
import { getSwapTradeCaptureQueue } from '@/store/swap/swapTradeCaptureQueue.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

// ██╗     ██╗███████╗████████╗███████╗███╗   ██╗███████╗██████╗ ███████╗
// ██║     ██║██╔════╝╚══██╔══╝██╔════╝████╗  ██║██╔════╝██╔══██╗██╔════╝
// ██║     ██║███████╗   ██║   █████╗  ██╔██╗ ██║█████╗  ██████╔╝███████╗
// ██║     ██║╚════██║   ██║   ██╔══╝  ██║╚██╗██║██╔══╝  ██╔══██╗╚════██║
// ███████╗██║███████║   ██║   ███████╗██║ ╚████║███████╗██║  ██║███████║
// ╚══════╝╚═╝╚══════╝   ╚═╝   ╚══════╝╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝╚══════╝

export const cashRegisterRepriceQuoteListener = (startAppListening: StartAppListening) => {
  startAppListening({
    actionCreator: cashRepriceQuote,
    // trigger trade capture call foreach product to populate them
    // then engages ESP
    effect: async (action, api) => {
      const {
        payload: { tileId, entry },
      } = action;

      const state = api.getState();

      switch (entry.product) {
        case 'Spot': {
          if (!state.cash[tileId]?.tradeCapture) {
            console.error(`No tradecapture state for cash product tileId=${tileId}`);
            return;
          }

          api.dispatch(
            initiateCheckCashByTradeCapture({
              tileId,
              type: 'FxSpot',
              properties: fxSpotTileProperties(entry),
            }),
          );
          break;
        }

        case 'Outright': {
          if (!state.cash[tileId]?.tradeCapture) {
            console.error(`No tradecapture state for cash product tileId=${tileId}`);
            return;
          }

          api.dispatch(
            initiateCheckCashByTradeCapture({
              tileId,
              type: 'FxFwd',
              properties: fxFwdTileProperties(entry),
            }),
          );
          break;
        }

        case 'Ndf': {
          if (!state.cash[tileId]?.tradeCapture) {
            console.error(`No tradecapture state for cash product tileId=${tileId}`);
            return;
          }

          api.dispatch(
            initiateCheckCashByTradeCapture({
              tileId,
              type: 'FxNdf',
              isNonDeliverable: true,
              properties: fxNdfTileProperties(entry),
            }),
          );
          break;
        }

        case 'Nds': // passtrough
        // Always producing "Swap" tile for "Nds"
        // Nds blotter entry has not been observed in devci
        // legacy fx produces feedback when executing a swapNds
        // but the associated blotter entry received via signal R has product == 'Swap", not "Nds"
        case 'Swap': {
          if (!state.swap[tileId]?.tradeCapture) {
            console.error(`No tradecapture state for swap product tileId=${tileId}`);
            return;
          }

          void getSwapTradeCaptureQueue(tileId).add(async () => {
            await api.dispatch(
              swapApi.endpoints.checkSwapByTradeCapture.initiate({
                tileId,
                properties: fxSwapTileProperties(entry),
                isNonDeliverable: false,
              }),
            );
          });
          break;
        }
        default:
          console.error('Unhandled reprice quote');
          return;
      }

      await effectOfAddTileActionOnESP(
        { payload: { tile: { tileId: action.payload.tileId } }, type: action.type },
        api,
      );
    },
  });
};

// ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
// ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
//    ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
//    ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
//    ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
//    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

const fxSpotTileProperties = (entry: BlotterCashEntry): FxCashSpot['properties'] => ({
  currencyPair: entry.currencyPair,
  currency1: entry.dealtCurrency,
  currency2: entry.contraCurrency,
  notional: entry.dealtAmount,
  maturityDateTenor: 'SP',
  notionalCurrencyIndex: (entry.currencyPair.startsWith(entry.dealtCurrency) ? 1 : 2) as CurrencyIndex,
});

const fxFwdTileProperties = (entry: BlotterCashEntry): FxCashForward['properties'] => ({
  currencyPair: entry.currencyPair,
  currency1: entry.dealtCurrency,
  currency2: entry.contraCurrency,
  notional: entry.dealtAmount,
  notionalCurrencyIndex: (entry.currencyPair.startsWith(entry.dealtCurrency) ? 1 : 2) as CurrencyIndex,
  maturityDate: entry.farDate ?? undefined,
});

// no reason to factor with previous function (AFAIK)
// NOSONAR
const fxNdfTileProperties = (entry: BlotterCashEntry): FxCashNdf['properties'] => ({
  currencyPair: entry.currencyPair,
  currency1: entry.dealtCurrency,
  currency2: entry.contraCurrency,
  notional: entry.dealtAmount,
  notionalCurrencyIndex: (entry.currencyPair.startsWith(entry.dealtCurrency) ? 1 : 2) as CurrencyIndex,
  maturityDate: entry.farDate ?? undefined,
});

const fxSwapTileProperties = (entry: BlotterCashEntry): FxSwap['properties'] => {
  const notionalCurrencyIndex = (entry.currencyPair.startsWith(entry.dealtCurrency) ? 1 : 2) as CurrencyIndex;

  return {
    type: entry.dealtAmount !== entry.farDealtAmount ? 'uneven' : 'standard',
    currencyPair: entry.currencyPair,
    currency1: entry.dealtCurrency,
    currency2: entry.contraCurrency,

    nearNotional: entry.dealtAmount ?? undefined,
    nearNotionalCurrencyIndex: notionalCurrencyIndex,
    nearExpiryDate: entry.nearDate ?? undefined,

    farNotional: entry.farDealtAmount ?? undefined,
    farNotionalCurrencyIndex: notionalCurrencyIndex,
    farExpiryDate: entry.farDate ?? undefined,
  };
};
